import { isEmpty, map } from 'lodash-es';
import type { QuoteFlowSection, QuoteFlowStep } from 'types/QuoteFlow';

import { setLocale } from 'yup';

// #region properties construction option images
import type { TransformedSubmission } from 'api/submission';
import AutoPaymentImg from 'assets/icons/auto-payment.svg';
import CoinsImg from 'assets/icons/coins.svg';
import FireResistiveImg from 'assets/images/construction_FireResistive.jpg';
import FrameImg from 'assets/images/construction_Frame.jpg';
import JoistedMasonryImg from 'assets/images/construction_JoistedMasonry.jpg';
import MasonryNonCombustibleImg from 'assets/images/construction_MasonryNon-Combustible.jpg';
import ModifiedFireResistiveImg from 'assets/images/construction_ModifiedFireResistive.jpg';
import NonCombustibleImg from 'assets/images/construction_Non-Combustible.jpg';
import type {
  CoverageCallout,
  CoverageCalloutAdditionalCriteria,
} from 'components/CoverageTable/CoverageTable.types';
import type { Field } from 'components/lib/QuestionEngine';
import { IS_EC_RAIL } from 'helpers/rail';
import { getManualPremiumPerilsFromSubmission, shouldSkipDriversStep } from 'helpers/utils';
import { DateTime } from 'luxon';
import type { FilloutFormShowCondition } from 'routes/Purchase/components/FilloutForm/FilloutForm.types';
import type { SkipCondition } from 'store/features/quoteStepper';
// #endregion

// #region Validation
export const VALIDATION_MESSAGES = {
  invalidDateFormat: 'Invalid date format',
  invalidEmailFormat: 'Invalid email format',
  invalidPhoneFormat: 'Invalid phone format',
  invalidVINFormat:
    'VIN must be exactly 17 characters and contain only numbers and letters excluding I, O, Q',
  dummyTaxIDValue:
    'Entering dummy information is against our terms of service. Enter accurate [FEIN/SSN].',
};

export const DEFAULT_MIN_STORIES_NUM = 1;
export const DEFAULT_MAX_STORIES_NUM = 200;
export const BUSINESS_DESCRIPTION_CHARACTER_LIMIT = 500;
export const AVERAGE_DAILY_ROOM_RATE_LIMIT = '10,000';

// Overwrite default yup validation messages
setLocale({
  mixed: {
    required: 'Required',
    default: 'Invalid value',
  },
  string: {
    email: VALIDATION_MESSAGES.invalidEmailFormat,
    min: 'Must be at least ${min} characters',
    max: 'Must be at most ${max} characters',
    length: 'This field should be ${length} digits',
    url: 'Invalid URL',
  },
  number: {
    min: 'Must be greater than or equal to ${min}',
    max: 'Must be less than or equal to ${max}',
  },
  date: {
    min: 'Must be later than ${min}',
    max: 'Must be earlier than ${max}',
  },
  array: {
    min: 'Must have at least ${min} items',
    max: 'Must have at most ${max} items',
  },
});

// The VIN standard is 17 characters long and contains numbers and letters excluding I, O, and Q, to avoid confusion with 1 and 0.
export const VIN_LENGTH = 17;
export const VIN_REGEX = /^[A-HJ-NPR-Z0-9]{17}$/;

// Pre-1981 vehicles were not required to carry a 17-character VIN, so a shorter VIN is accepted and
// sent to underwriting instead of being rejected (ENG-12346).
export const SHORT_VIN_INFO_MESSAGE = 'VINs less than 17 characters will require additional review';
export const MISSING_VEHICLE_VIN_MODAL_TITLE = 'Missing VIN';
export const MISSING_VEHICLE_VIN_MODAL_DESCRIPTION =
  'The missing VIN only allows for a provisional price indication. You will be required to enter a valid VIN later.';
export const TRAILER_BODY_CATEGORY = 'Trailer';
// #endregion

export const STATE_ZIPCODE_REGEX = /^([A-Za-z\s]+)\s+(\d{5})$/;
export const ZIPCODE_LENGTH = 5;
export const ADDRESS_LINE_MAX_LENGTH = 64;

export const firstDayOfYear1900 = 'Tue Jan 01 1900 07:00:00 GMT+0300 (GMT+03:00)';
export const defaultCurrency = 'USD';
export const defaultDateformat = 'MM/dd/yyyy';
export const defaultBackendDateformat = 'yyyy-MM-dd';
export const dateFormatWithMonthName = 'MMMM dd, yyyy';
export const dateQueryParamFormat = 'MM-dd-yyyy';

export const META = {
  HOME_LINK: 'https://threeinsurance.com',
  MYTHREE_ONLINE_LINK:
    import.meta.env.REACT_APP_MYTHREE_ONLINE_URL ??
    'https://preview.threeinsurance.com/my-three-online/',
  PRIVACY_POLICY_LINK: 'https://threeinsurance.com/privacy-policy',
  TERMS_CONDITIONS_LINK: 'https://threeinsurance.com/terms-conditions',
  THE_POLICY_LINK: 'https://threeinsurance.com/the-policy',
  CONTACT_NUMBER: '1-800-507-4495',
  PRIORITY_CONTACT_NUMBER: '1-800-576-4349',
  SERVICE_EMAIL: 'service@threeinsurance.com',
};

export const STATES = {
  AK: 'AK',
  AL: 'AL',
  AR: 'AR',
  AZ: 'AZ',
  CA: 'CA',
  CO: 'CO',
  CT: 'CT',
  DC: 'DC',
  DE: 'DE',
  FL: 'FL',
  GA: 'GA',
  HI: 'HI',
  IA: 'IA',
  ID: 'ID',
  IL: 'IL',
  IN: 'IN',
  KS: 'KS',
  KY: 'KY',
  LA: 'LA',
  MA: 'MA',
  MD: 'MD',
  ME: 'ME',
  MI: 'MI',
  MN: 'MN',
  MO: 'MO',
  MS: 'MS',
  MT: 'MT',
  NC: 'NC',
  ND: 'ND',
  NE: 'NE',
  NH: 'NH',
  NJ: 'NJ',
  NM: 'NM',
  NV: 'NV',
  NY: 'NY',
  OH: 'OH',
  OK: 'OK',
  OR: 'OR',
  PA: 'PA',
  RI: 'RI',
  SC: 'SC',
  SD: 'SD',
  TN: 'TN',
  TX: 'TX',
  UT: 'UT',
  VA: 'VA',
  VT: 'VT',
  WA: 'WA',
  WI: 'WI',
  WV: 'WV',
  WY: 'WY',
};

export const STATES_FULLNAME = {
  AK: 'Alaska',
  AL: 'Alabama',
  AR: 'Arkansas',
  AZ: 'Arizona',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DC: 'District of Columbia',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  IA: 'Iowa',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  MA: 'Massachusetts',
  MD: 'Maryland',
  ME: 'Maine',
  MI: 'Michigan',
  MN: 'Minnesota',
  MO: 'Missouri',
  MS: 'Mississippi',
  MT: 'Montana',
  NC: 'North Carolina',
  ND: 'North Dakota',
  NE: 'Nebraska',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NV: 'Nevada',
  NY: 'New York',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VA: 'Virginia',
  VT: 'Vermont',
  WA: 'Washington',
  WI: 'Wisconsin',
  WV: 'West Virginia',
  WY: 'Wyoming',
};

export const STATE_SET = new Set(Object.values(STATES));

export const STATE_LIST = map(STATES, (state) => ({ code: state, name: state }));

/**
 * Answer options for the MN/NJ State Unemployment Number questions (ENG-11182). Only "Yes" asks for
 * the number itself — the other three record why the employer cannot supply one, and Socotra fills
 * the reporting sentinel for those.
 */
const STATE_UNEMPLOYMENT_NUMBER_CHOICES = [
  { code: 'Yes', name: 'Yes' },
  {
    code: 'Business is exempted or otherwise not subject to the provisions of the Unemployment Compensation Law',
    name: 'Business is exempted or otherwise not subject to the provisions of the Unemployment Compensation Law',
  },
  { code: 'Employer is unaware of this number', name: 'Employer is unaware of this number' },
  { code: "I don't have this number right now", name: "I don't have this number right now" },
];

/**
 * The "About you" fields on the EC rail.
 *
 * EC keeps business contact in backend columns, not in the question set (ENG-12068). So on EC
 * "About you" is a hardcoded step with these fields, and it does not need any question config.
 * The agent portal does the same with its own contact section.
 *
 * The backend still saves these answers and sends them back under `uw_questions`.
 * Email and website are not here. The flow asks for the email at the start, and the website is
 * on the "About your business" step.
 */
export const EC_BUSINESS_CONTACT_FIELDS: Field[] = [
  {
    code: 'contactFirst',
    name: 'First Name',
    is_optional: false,
    is_hidden: false,
    is_readonly: false,
    newline: false,
    question_text: "Let's start with your name and phone number",
    hide_asteriks_in_question_text: true,
    type: 'string',
  },
  {
    code: 'contactLast',
    name: 'Last Name',
    is_optional: false,
    is_hidden: false,
    is_readonly: false,
    newline: false,
    type: 'string',
  },
  {
    code: 'contactPhone',
    name: 'Phone Number',
    is_optional: false,
    is_hidden: false,
    is_readonly: false,
    newline: false,
    type: 'phone',
  },
];

export const APPLICATION_STEPS = {
  // #region Business info
  business_contact: IS_EC_RAIL
    ? {
        label: 'About you',
        key: 'business_contact',
        type: 'hardcoded',
        fields: EC_BUSINESS_CONTACT_FIELDS,
      }
    : { label: 'About you', key: 'business_contact', type: 'dynamic' },
  legal: {
    label: 'About your business',
    key: 'legal',
    type: 'hardcoded',
    fields: [
      {
        code: 'businessName',
        name: 'Legal Business Name',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        question_text: 'What is the name of your business?',
        hide_asteriks_in_question_text: true,
        type: 'string',
        additional_data: {
          socotra_field_code: 'pol_named_insured',
        },
      },
      {
        code: 'dba',
        name: 'DBA',
        is_optional: true,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'string',
      },
      {
        code: 'businessWebsite',
        name: 'Business Website',
        is_optional: true,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'string',
      },
      {
        code: 'entityType',
        name: 'Business Structure',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'select',
        question_text: 'How’s your business structured?',
        tooltip:
          'This is the legal structure set up for your business when registering with the state. It has ownership, liability and tax implications.',
        hide_asteriks_in_question_text: true,
        additional_data: {
          socotra_field_code: 'pol_legal_entity',
        },
        choices: [
          {
            code: 'SoleProprietor',
            name: 'Sole Proprietor',
          },
          {
            code: 'LLC',
            name: 'LLC',
          },
          {
            code: 'LimitedPartnership',
            name: 'Limited Partnership',
          },
          {
            code: 'Partnership',
            name: 'Partnership',
          },
          {
            code: 'Corporation',
            name: 'Corporation - C Corp',
          },
          {
            code: 'CorporationSCorp',
            name: 'Corporation - S Corp',
          },
          {
            code: 'CorporationOther',
            name: 'Corporation - Other',
          },
          {
            code: 'NonprofitOrganization',
            name: 'Nonprofit Org',
          },
          {
            code: 'Trust',
            name: 'Trust',
          },
        ],
      },
      {
        code: 'trustBeneficiaryName',
        name: 'Trust Beneficiary Name',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        question_text: 'Trust Beneficiary',
        show_condition: {
          reference: 'entityType',
          relational_operator: '==',
          value: 'Trust',
          value_type: 'string',
        },
        newline: false,
        type: 'string',
      },
      {
        code: 'trustBeneficiaryAddress',
        name: 'Trust Beneficiary Address',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        show_condition: {
          reference: 'entityType',
          relational_operator: '==',
          value: 'Trust',
          value_type: 'string',
        },
        newline: false,
        type: 'string',
      },
      {
        code: 'addAdditionalBeneficiary',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        question_text: 'Add Additional Beneficiary',
        show_condition: {
          reference: 'entityType',
          relational_operator: '==',
          value: 'Trust',
          value_type: 'string',
        },
        newline: false,
        type: 'select_button',
        choices: [
          {
            code: 'Yes',
            name: 'Yes',
          },
          {
            code: 'No',
            name: 'No',
          },
        ],
      },
      {
        code: 'trustBeneficiaryName2',
        name: 'Trust Beneficiary Name',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        question_text: 'Additional Trust Beneficiary',
        show_condition: {
          reference: 'addAdditionalBeneficiary',
          relational_operator: '==',
          value: 'Yes',
          value_type: 'string',
        },
        newline: false,
        type: 'string',
      },
      {
        code: 'trustBeneficiaryAddress2',
        name: 'Trust Beneficiary Address',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        show_condition: {
          reference: 'addAdditionalBeneficiary',
          relational_operator: '==',
          value: 'Yes',
          value_type: 'string',
        },
        newline: false,
        type: 'string',
      },
      {
        code: 'yearFounded',
        name: 'Year Business Operations Began',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        question_text: 'Year Business Operations Began?',
        tooltip: '',
        hide_asteriks_in_question_text: true,
        newline: false,
        type: 'year',
        additional_data: {
          disableFuture: true,
          socotra_field_code: 'pol_year_founded',
          disableDateBefore1900: true,
        },
      },
      {
        code: 'yearsExperience',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        name: 'Years of Experience',
        question_text:
          'How many years of experience does most senior owner or officer have in their profession?',
        show_condition: {
          reference: 'pol_tax_industry',
          relational_operator: '==',
          value: 'Construction & Specialty Trade Contractors',
          value_type: 'string',
        },
        newline: false,
        type: 'number',
        additional_data: {
          socotra_field_code: 'pol_years_prof_experience',
          max: '999',
          min: '0',
        },
      },
      {
        code: 'taxIDType',
        name: 'Tax ID Type',
        is_optional: false,
        is_hidden: true,
        is_readonly: true,
        newline: false,
        question_text: 'What’s your business’ tax ID type?',
        hide_asteriks_in_question_text: true,
        type: 'select',
        choices: [
          {
            code: 'FEIN',
            name: 'FEIN',
          },
          {
            code: 'Social Security Number',
            name: 'Social Security Number',
          },
        ],
      },
      {
        code: 'hasCSLB',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        question_text:
          'Does the business have a license number from the Contractors State License Board?',
        show_condition: {
          operations: [
            {
              operations: [
                {
                  reference: 'pol_state',
                  relational_operator: '==',
                  value: 'CA',
                  value_type: 'string',
                },
                {
                  operations: [
                    {
                      reference: 'pol_state',
                      relational_operator: '==',
                      value: 'CA',
                      value_type: 'string',
                    },
                    {
                      reference: 'pol_tax_wc_industry',
                      relational_operator: '==',
                      value: 'Construction & Specialty Trade Contractors',
                      value_type: 'string',
                    },
                  ],
                  logical_operator: 'and',
                },
              ],
              logical_operator: 'and',
            },
            {
              operations: [
                {
                  reference: 'pol_state',
                  relational_operator: '==',
                  value: 'CA',
                  value_type: 'string',
                },
                {
                  operations: [
                    {
                      reference: 'pol_state',
                      relational_operator: '==',
                      value: 'CA',
                      value_type: 'string',
                    },
                    {
                      reference: 'pol_tax_wc_subindustry',
                      relational_operator: 'in',
                      value: ['Landscaping', 'Installation/Repair'],
                      value_type: 'array',
                    },
                  ],
                  logical_operator: 'and',
                },
              ],
              logical_operator: 'and',
            },
            {
              operations: [
                {
                  reference: 'pol_state',
                  relational_operator: '==',
                  value: 'CA',
                  value_type: 'string',
                },
                {
                  operations: [
                    {
                      reference: 'pol_state',
                      relational_operator: '==',
                      value: 'CA',
                      value_type: 'string',
                    },
                    {
                      reference: 'pol_tax_wc_desc',
                      relational_operator: 'in',
                      value: [
                        'Mobile Crane Rental with Operators',
                        'Sewer or Hazardous Spill Cleaning',
                        'Traffic Control',
                      ],
                      value_type: 'array',
                    },
                  ],
                  logical_operator: 'and',
                },
              ],
              logical_operator: 'and',
            },
          ],
          logical_operator: 'or',
        },
        newline: false,
        type: 'select_button',
        choices: [
          {
            code: 'Yes',
            name: 'Yes',
          },
          {
            code: 'No',
            name: 'No',
          },
        ],
      },
      {
        code: 'CSLBNumber',
        name: 'License Number',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        show_condition: {
          operations: [
            {
              operations: [
                {
                  reference: 'pol_state',
                  relational_operator: '==',
                  value: 'CA',
                  value_type: 'string',
                },
                {
                  reference: 'hasCSLB',
                  relational_operator: '==',
                  value: 'Yes',
                  value_type: 'string',
                },
              ],
              logical_operator: 'and',
            },
            {
              reference: 'pol_state',
              relational_operator: '==',
              value: 'CA',
              value_type: 'string',
            },
          ],
          logical_operator: 'and',
        },
        newline: false,
        type: 'string',
      },
      {
        code: 'hasNJTIN',
        name: 'NJTIN',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        question_text: 'Does the business have a New Jersey Tax Identification Number (NJTIN)?',
        show_condition: {
          reference: 'pol_state',
          relational_operator: '==',
          value: 'NJ',
          value_type: 'string',
        },
        newline: false,
        type: 'select',
        choices: STATE_UNEMPLOYMENT_NUMBER_CHOICES,
      },
      {
        code: 'NJTIN',
        name: 'New Jersey Tax Identification Number (NJTIN)',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        question_text: 'What is the New Jersey Tax Identification Number (NJTIN)?',
        show_condition: {
          operations: [
            {
              reference: 'pol_state',
              relational_operator: '==',
              value: 'NJ',
              value_type: 'string',
            },
            {
              reference: 'hasNJTIN',
              relational_operator: '==',
              value: 'Yes',
              value_type: 'string',
            },
          ],
          logical_operator: 'and',
        },
        newline: false,
        type: 'string',
        additional_data: {
          minLength: 12,
          maxLength: 12,
        },
      },
      {
        code: 'hasMNSEIN',
        name: 'MNSEIN',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        question_text: 'Does the Employer have a Minnesota State Employer Identification Number?',
        show_condition: {
          reference: 'pol_state',
          relational_operator: '==',
          value: 'MN',
          value_type: 'string',
        },
        newline: false,
        type: 'select',
        choices: STATE_UNEMPLOYMENT_NUMBER_CHOICES,
      },
      {
        code: 'MNSEIN',
        name: 'Minnesota Employer Identification Number (MNSEIN)',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        question_text: 'What is the Minnesota Employer Identification Number (MNSEIN)?',
        show_condition: {
          operations: [
            {
              reference: 'pol_state',
              relational_operator: '==',
              value: 'MN',
              value_type: 'string',
            },
            {
              reference: 'hasMNSEIN',
              relational_operator: '==',
              value: 'Yes',
              value_type: 'string',
            },
          ],
          logical_operator: 'and',
        },
        newline: false,
        type: 'string',
        additional_data: {
          minLength: 15,
          maxLength: 15,
        },
      },
      {
        code: 'ssn',
        name: 'Social Security Number (SSN)',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        show_condition: {
          reference: 'taxIDType',
          relational_operator: '==',
          value: 'Social Security Number',
          value_type: 'string',
        },
        newline: false,
        type: 'string',
        additional_data: {
          socotra_field_code: 'pol_ssn',
          fs_mask: true,
        },
      },
    ],
  },
  'management_&_financials': {
    label: 'Management and financials',
    key: 'management_&_financials',
    type: 'dynamic',
  },
  // #endregion

  // #region Location
  location: {
    label: 'About your location',
    key: 'location',
    type: 'hardcoded',
  },
  // #endregion

  // #region Operations
  prohibited_exposures: {
    label: 'Prohibited exposures',
    key: 'prohibited_exposures',
    type: 'dynamic',
  },
  classification: { label: 'Classification', key: 'classification', type: 'dynamic' },
  property: {
    label: 'Property',
    key: 'property',
    type: 'dynamic',
  },
  liability: { label: 'Liability', key: 'liability', type: 'dynamic' },
  professional_services: {
    label: 'Professional Services',
    key: 'professional_services',
    type: 'dynamic',
  },
  cyber: { label: 'Cybersecurity', key: 'cyber', type: 'dynamic' },
  'employee_&_subcontractor_activities': {
    label: 'Employee & Subcontractor Activities',
    key: 'employee_&_subcontractor_activities',
    type: 'dynamic',
  },
  auto: { label: 'Auto', key: 'auto', type: 'dynamic' },
  // #endregion

  // #region Employees
  owners: {
    label: 'Owners',
    key: 'owners',
    type: 'hardcoded',
    fields: [
      {
        code: 'pol_oo_name_first',
        name: 'First name',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'string',
      },
      {
        code: 'pol_oo_name_last',
        name: 'Last name',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'string',
      },
      {
        code: 'pol_oo_dob',
        name: 'Date of birth',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'date',
        additional_data: {
          disableFuture: true,
          monthDayYearView: true,
        },
      },
      {
        code: 'pol_oo_isprimary',
        name: 'Primary owner',
        is_optional: true,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'checkbox',
        question_text: ' ',
      },
      {
        code: 'pol_oo_home_address',
        name: 'Street address, City, State, ZIP Code',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'string',
        question_text: 'What is their home address?',
      },
      {
        code: 'pol_oo_ncf_optin',
        name: 'Authorize that a soft credit check be run on this individual in order to save up to 40%.',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'checkbox',
        additional_data: {
          descriptionList: [
            'The credit check will be run upon Quoting and only if the quote includes scheduled vehicles.',
            "This soft credit check will not impact the individual's credit score.",
          ],
        },
      },
    ],
  },
  // #endregion

  // #region properties
  'before-property': { label: 'Your Properties', key: 'before-property', type: 'dynamic' },
  properties: {
    label: 'Properties',
    key: 'building',
    type: 'hardcoded',
    fields: [
      {
        code: 'bdg_name',
        name: 'Building Name',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'string',
        question_text: "Give this building a descriptive name so it's easy to identify.",
        tooltip: 'Each building will get its own nickname.',
      },
      {
        code: 'bdg_occupancy',
        name: 'Your Space',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'select',
        choices: [
          {
            code: 'Home/Apartment',
            name: 'Home Office',
          },
          {
            code: 'Kiosk',
            name: 'Kiosk',
          },
          {
            code: 'Shared/coworking space',
            name: 'Shared/coworking space',
          },
          {
            code: 'Commercial condo I lease',
            name: 'Commercial condo I lease',
          },
          {
            code: 'Commercial condo I own',
            name: 'Commercial condo I own',
          },
          {
            code: 'Building I lease and am not required to insure',
            name: 'Building I lease and am not required to insure',
          },
          {
            code: "Building I lease and am required to insure (e.g. 'Triple Net Lease')",
            name: "Building I lease and am required to insure (e.g. 'Triple Net Lease')",
          },
          {
            code: 'Building I own and run my business out of',
            name: 'Building I own and run my business out of',
          },
          {
            code: 'Building I own/manage and lease to others',
            name: 'Building I own/manage and lease to others',
          },
        ],
        question_text: 'What type of space do you run your business operations from?',
        additional_data: {
          emitOnChangedEvent: true,
        },
      },
      {
        code: 'bdg_construction_code',
        name: 'Construction',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'select_button',
        choices: [
          {
            code: '1',
            name: 'Frame',
            description:
              'Buildings with exterior walls, floors and roofs of combustible material - typically wood.',
            fillType: 'full',
            endAdornment: {
              src: FrameImg,
              type: 'image',
            },
          },
          {
            code: '2',
            name: 'Joisted Masonry',
            description:
              'Buildings with exterior walls, constructed of a material such as brick, hollow or solid concrete, block, concrete gypsum black, clay tile, stone or similar materials.',
            fillType: 'full',
            endAdornment: {
              src: JoistedMasonryImg,
              type: 'image',
            },
          },
          {
            code: '3',
            name: 'Non Combustible',
            description:
              'Buildings with exterior walls, floors and roof of noncombustible or slow-burning materials.',
            fillType: 'full',
            endAdornment: {
              src: NonCombustibleImg,
              type: 'image',
            },
          },
          {
            code: '4',
            name: 'Masonry Non Combustible',
            description:
              'Buildings with walls made of masonry, consisting of concrete block, reinforced masonry and can be combined with steel framing.',
            fillType: 'full',
            endAdornment: {
              src: MasonryNonCombustibleImg,
              type: 'image',
            },
          },
          {
            code: '5',
            name: 'Modified Fire Resistive',
            description:
              'Similar to a fire resistive structure but the masonry used for the exterior walls, floors, and roof is thinner. It has a fire resistance rating of between 1 and 2 hours.',
            fillType: 'full',
            endAdornment: {
              src: ModifiedFireResistiveImg,
              type: 'image',
            },
          },
          {
            code: '6',
            name: 'Fire Resistive',
            description:
              'Buildings with exterior walls, floors and roof  of masonry or fire-resistive materials with a fire resistance rating of at least 2 hours.',
            fillType: 'full',
            endAdornment: {
              src: FireResistiveImg,
              type: 'image',
            },
          },
        ],
        question_text: 'What is the construction of the building?',
      },
      {
        code: 'bdg_year_built',
        name: 'Year Built',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'year',
        additional_data: {
          disableFuture: true,
          disableDateLessThan4Digits: true,
        },
        question_text: 'What year was the property built?',
      },
      {
        code: 'bdg_sqft',
        name: 'Square Footage',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'number',
        question_text: 'What is the square footage of the building?',
        tooltip:
          'No need for blueprints, we only want to know about the part of the building your business occupies.',
        additional_data: {
          min: '1',
          allowDecimal: false,
        },
      },
      {
        code: 'bdg_num_stories',
        name: 'Stories',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'number',
        question_text: 'How many stories does the building have?',
        additional_data: {
          allowDecimal: false,
        },
      },
      {
        code: 'bdg_issprinklered',
        name: 'Automatic Sprinkler System',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'select_button',
        choices: [
          {
            code: 'Yes',
            name: 'Yes',
          },
          {
            code: 'No',
            name: 'No',
          },
        ],
        question_text: 'Is the building protected by an automatic sprinkler system?',
      },
      {
        code: 'isSprinklerInspectedAnnually',
        name: 'Sprinkler System Inspection',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'select_button',
        choices: [
          {
            code: 'Yes',
            name: 'Yes',
          },
          {
            code: 'No',
            name: 'No',
          },
        ],
        question_text: 'Is the sprinkler system inspected and serviced annually?',
        show_condition: {
          reference: 'bdg_issprinklered',
          relational_operator: '==',
          value: 'Yes',
          value_type: 'string',
        },
      },
      {
        code: 'sprinklerInspectionAcknowledgement',
        name: 'Sprinkler Inspection Documentation',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'select',
        choices: [
          {
            code: 'I acknowledge',
            name: 'I acknowledge',
          },
        ],
        question_text:
          'Please note that if you purchase this policy you will be required to submit documentation of the most recent inspection.',
        show_condition: {
          reference: 'isSprinklerInspectedAnnually',
          relational_operator: '==',
          value: 'Yes',
          value_type: 'string',
        },
      },
    ],
  },
  'uw-property': {
    label: 'About your properties',
    key: 'uw-property',
    type: 'dynamic',
  },
  // #endregion

  // #region Workers' Compensation & Individual Owners
  workers_compensation: {
    label: "Workers' Compensation",
    key: 'workers_compensation',
    type: 'hardcoded',
  },

  individual_owners: {
    label: 'Individual Owners',
    key: 'individual_owners',
    type: 'hardcoded',
    fields: [
      {
        code: 'pol_oo_wc_excluded',
        name: 'Excluded',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        type: 'boolean',
      },
      {
        code: 'pol_oo_wc_class_code',
        name: 'Job Duty',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        type: 'string',
      },
      {
        code: 'pol_oo_wc_payroll',
        name: 'Payroll',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        type: 'currency',
      },
    ],
  },
  // #endregion

  // #region Vehicles & drivers
  vehicles: {
    label: 'Vehicles',
    key: 'vehicles',
    type: 'hardcoded',
    fields: [
      {
        code: 'veh_location',
        name: 'Garaging Location',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        type: 'string',
      },
      {
        code: 'is_vin_number_available',
        name: 'VIN Number',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        type: 'select_button',
        choices: [
          {
            code: 'Yes',
            name: 'Yes',
          },
          {
            code: 'No',
            name: 'No',
          },
        ],
        question_text: 'Do you have a VIN number available?',
      },
      {
        code: 'veh_vin',
        name: 'VIN Number',
        is_optional: true,
        is_hidden: false,
        is_readonly: false,
        type: 'string',
        show_condition: {
          reference: 'is_vin_number_available',
          relational_operator: '==',
          value: 'Yes',
          value_type: 'string',
        },
      },
      {
        code: 'veh_body_category',
        name: 'Body Category',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        type: 'select',
        question_text: 'Select a vehicle type',
      },
      {
        code: 'veh_body_subcategory',
        name: 'Body type',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        type: 'select',
      },
      {
        code: 'veh_year',
        name: 'Year',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        type: 'select',
        choices: Array.from({ length: 2024 - 1960 }, (_, i) => ({
          code: `${2024 - i}`,
          name: `${2024 - i}`,
        })),
      },
      {
        code: 'veh_make',
        name: 'Make',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        type: 'select',
        additional_data: {
          loading: false,
        },
      },
      {
        code: 'veh_model',
        name: 'Model',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        type: 'select',
        additional_data: {
          loading: false,
        },
      },
      {
        code: 'veh_trim',
        name: 'Trim',
        is_optional: true,
        is_hidden: false,
        is_readonly: false,
        type: 'string',
        additional_data: {
          loading: false,
        },
      },
      {
        code: 'veh_radius',
        name: 'Vehicle Radius',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        type: 'select',
        choices: [
          {
            code: '0 to 50',
            name: '0 to 50',
          },
          {
            code: '51 to 100',
            name: '51 to 100',
          },
          {
            code: '101 to 200',
            name: '101 to 200',
          },
          {
            code: '201 to 499',
            name: '201 to 499',
          },
          {
            code: '500 to 999',
            name: '500 to 999',
          },
          {
            code: '1000+',
            name: '1000+',
          },
        ],
      },
      {
        code: 'veh_stated_amt',
        name: 'Value',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        type: 'currency',
        question_text: 'If you sold this vehicle today, how much would it be worth?',
      },
      {
        code: 'veh_limit',
        name: 'Vehicle coverage limit',
        is_optional: true,
        is_hidden: true,
        is_readonly: false,
        type: 'number',
      },
      {
        code: 'veh_deductible',
        name: 'Vehicle coverage deductible',
        is_optional: true,
        is_hidden: true,
        is_readonly: false,
        type: 'select',
        choices: [
          {
            code: '500',
            name: '500',
          },
          {
            code: '1000',
            name: '1,000',
          },
          {
            code: '2500',
            name: '2,500',
          },
        ],
      },
      {
        code: 'veh_gvw',
        name: 'Gross (Combined) Vehicle Weight',
        is_optional: false,
        is_hidden: false,
        is_readonly: true,
        type: 'select',
        choices: [
          {
            code: '0 - 10,000 lb',
            name: '0 - 10,000 lb',
          },
          {
            code: '10,000 - 20,000 lb',
            name: '10,000 - 20,000 lb',
          },
          {
            code: '20,000 - 45,000 lb',
            name: '20,000 - 45,000 lb',
          },
          {
            code: '45,000+ lb',
            name: '45,000+ lb',
          },
        ],
      },
      {
        code: 'veh_gvwr',
        name: 'Gross Vehicle Weight Rating',
        is_optional: true,
        is_hidden: true,
        is_readonly: false,
        type: 'string',
      },
      {
        code: 'veh_gcwr',
        name: 'Gross (Combined) Vehicle Weight Rating',
        is_optional: true,
        is_hidden: true,
        is_readonly: false,
        type: 'string',
      },
      {
        code: 'veh_msrp',
        name: 'Cost New (MSRP)',
        is_optional: true,
        is_hidden: false,
        is_readonly: true,
        type: 'number',
      },
      {
        code: 'veh_type',
        name: 'Type',
        is_optional: true,
        is_hidden: true,
        is_readonly: false,
        type: 'string',
      },
      {
        code: 'veh_dt_type',
        name: 'DT_Type',
        is_optional: true,
        is_hidden: true,
        is_readonly: false,
        type: 'string',
      },
      {
        code: 'veh_subtype',
        name: 'Subtype',
        is_optional: true,
        is_hidden: true,
        is_readonly: false,
        type: 'string',
      },
      {
        code: 'veh_ttt_use',
        name: 'Vehicle TTT Use',
        is_optional: true,
        is_hidden: true,
        is_readonly: false,
        type: 'string',
      },
      {
        code: 'veh_ppt_use',
        name: 'Vehicle PPT Use',
        is_optional: true,
        is_hidden: true,
        is_readonly: false,
        type: 'string',
      },
      {
        code: 'veh_class',
        name: 'Vehicle Class',
        is_optional: true,
        is_hidden: true,
        is_readonly: false,
        type: 'select',
      },
      {
        code: 'veh_seat_capacity',
        name: 'Vehicle Seat Capacity',
        is_optional: true,
        is_hidden: true,
        is_readonly: false,
        type: 'string',
      },
      {
        code: 'veh_msrp_dpr',
        name: 'Depreciated Cost New',
        is_optional: true,
        is_hidden: false,
        is_readonly: true,
        type: 'number',
      },
    ],
  },
  vehicles_injury_protection: {
    label: 'Michigan Personal Injury Protection (PIP) Coverage Selection',
    key: 'vehicles_injury_protection',
    type: 'hardcoded',
    fields: [
      {
        code: 'pol_pip_mi_total_medical_expenses',
        name: 'PIP Medical Coverage',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'select',
        question_text:
          'Please select your Personal Injury Protection (PIP) Medical Coverage limit:',
        choices: [
          {
            code: '250000',
            name: '250,000',
          },
          {
            code: '500000',
            name: '500,000',
          },
          {
            code: '99999999',
            name: 'Unlimited',
          },
        ],
        additional_data: {
          emitOnChangedEvent: true,
        },
      },
      {
        code: 'pol_pip_mi_excess_attendant_care',
        name: 'Excess Attendant Care',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'select',
        question_text: 'Please select your Excess Attendant Care limit:',
        choices: [
          {
            code: '0',
            name: '0',
          },
          {
            code: '10000',
            name: '10,000',
          },
        ],
        show_condition: {
          operations: [
            {
              reference: 'pol_pip_mi_total_medical_expenses',
              relational_operator: '==',
              value: '500000',
              value_type: 'string',
            },
            {
              reference: 'pol_pip_mi_total_medical_expenses',
              relational_operator: '==',
              value: '250000',
              value_type: 'string',
            },
          ],
          logical_operator: 'or',
        },
      },
    ],
  },
  drivers: {
    label: 'Drivers',
    key: 'drivers',
    type: 'hardcoded',
    fields: [
      {
        code: 'pol_drivers_first',
        name: 'First Name',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'string',
      },
      {
        code: 'pol_drivers_last',
        name: 'Last Name',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'string',
      },
      {
        code: 'pol_drivers_dob',
        name: 'Date of Birth',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'date',
        precision: 'day',
        additional_data: {
          disableFuture: true,
          monthDayYearView: true,
        },
      },
      {
        code: 'pol_drivers_license_state',
        name: 'State',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'select',
        choices: STATE_LIST,
        additional_data: {
          useConfigStates: true,
        },
      },
      {
        code: 'pol_drivers_license_number',
        name: 'License Number',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'string',
      },
      {
        code: 'pol_drivers_cdl_license_type',
        name: 'CDL license type',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'select',
        choices: [
          {
            code: 'A',
            name: 'A',
          },
          {
            code: 'B',
            name: 'B',
          },
          {
            code: 'C',
            name: 'C',
          },
          {
            code: 'Other',
            name: 'Other',
          },
          {
            code: 'Not sure',
            name: 'Not sure',
          },
          {
            code: 'None',
            name: 'None',
          },
        ],
      },
      {
        code: 'pol_drivers_cdl_license_year',
        name: 'CDL license year',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'select',
        choices: [
          {
            code: 'Less than a year',
            name: 'Less than a year',
          },
          {
            code: 'At least 1 year but less than 2 years',
            name: 'At least 1 year but less than 2 years',
          },
          {
            code: '2 or more years',
            name: '2 or more years',
          },
          {
            code: 'None',
            name: 'None',
          },
        ],
      },
    ],
  },
  'driving-incidents': {
    label: 'Driving incidents',
    key: 'driving-incidents',
    type: 'hardcoded',
    fields: [
      {
        code: 'pol_incidents_driver',
        name: 'Driver',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'select',
        choices: [],
      },
      {
        code: 'pol_incidents_date',
        name: 'Date of Incident',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'date',
        precision: 'day',
        additional_data: {
          disableFuture: true,
        },
      },
      {
        code: 'pol_incidents_type',
        name: 'Incident Type 1',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'group',
        nested_fields: [],
      },
      {
        code: 'pol_incidents_desc',
        name: 'Describe other violation',
        is_optional: true,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'string',
        additional_data: {
          otherDescConditionalFieldname: {
            code: 'pol_incidents_type',
            value: [
              'Other non-moving violation',
              'Other - minor moving violation',
              'Other violation - felony',
            ],
          },
        },
      },
    ],
  },
  // #endregion

  // #region Claims
  claims: {
    label: 'Claims',
    key: 'claims',
    type: 'hardcoded',
    fields: [
      {
        code: 'pol_claims_date',
        name: 'Date of Loss',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'date',
        precision: 'day',
        additional_data: {
          disableFuture: true,
        },
      },
      {
        code: 'pol_claims_type',
        name: 'Claim Type',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'select',
        choices: [
          {
            code: 'Auto Accident',
            name: 'Auto Accident',
          },
          {
            code: 'Other Vehicle Damage Claim (not related to an auto accident)',
            name: 'Other Vehicle Damage Claim (not related to an auto accident)',
          },
          {
            code: 'Other Property Damage Claim',
            name: 'Other Property Damage Claim',
          },
          {
            code: 'Workers Compensation Claim',
            name: 'Workers Compensation Claim',
          },
          {
            code: 'Other Liability Claim',
            name: 'Other Liability Claim',
          },
        ],
      },
      {
        code: 'pol_claims_amount',
        name: 'Approximate Cost',
        is_optional: false,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'select',
        choices: [
          {
            code: '$0 - $4,999',
            name: '$0 - $4,999',
          },
          {
            code: '$5,000 - $24,999',
            name: '$5,000 - $24,999',
          },
          {
            code: '$25,000 - $99,999',
            name: '$25,000 - $99,999',
          },
          {
            code: '>= $100,000',
            name: '>= $100,000',
          },
        ],
      },
      {
        code: 'pol_claims_desc',
        name: 'Claim Description',
        is_optional: true,
        is_hidden: false,
        is_readonly: false,
        newline: false,
        type: 'string',
        additional_data: {
          multiline: true,
        },
      },
    ],
  },
  // #endregion
} satisfies { [key: string]: QuoteFlowStep };

export const QUOTE_FLOW_SECTION_KEYS = {
  APPLICATION: 'application',
  QUOTE: 'quote',
  PURCHASE: 'purchase',
};

export const QUOTE_FLOW_SECTIONS: QuoteFlowSection[] = [
  {
    label: 'Application',
    key: QUOTE_FLOW_SECTION_KEYS.APPLICATION,
    steps: map(APPLICATION_STEPS, (step) => step),
  },
  { label: 'Quote', key: QUOTE_FLOW_SECTION_KEYS.QUOTE },
  { label: 'Purchase', key: QUOTE_FLOW_SECTION_KEYS.PURCHASE },
];

export const defaultDebounceDelay = 600;

export const employerExposurePerilNames = {
  MANUAL_PREMIUM: 'manual_premium',
  SMALL_DEDUCTIBLE_PROGRAM: 'small_deductible_program',
} as const;

export const NUM_EMPLOYEES_FIELD_CODE = 'numEmployees';
export const BUSINESS_WEBSITE = 'businessWebsite';

export const SUBCONTRACTORS_FIELD_CODE = 'subcontractors';
export const CALL_FOR_XMOD_FAILED_QUESTION_CODE = 'callForXMODFailed';
export const FEIN_FIELD_CODE = 'fein';
export const SSN_FIELD_CODE = 'ssn';
export const TAX_ID_TYPE_FIELD_CODE = 'taxIDType';
export const TAX_ID_TYPE_CHOICE_EMPTY_FIELD_CODE = 'I don’t have a FEIN/SSN right now';
export const TAX_ID_TYPE_CHOICE_FEIN_FIELD_CODE = 'FEIN';
export const TAX_ID_TYPE_CHOICE_SSN_FIELD_CODE = 'Social Security Number';
export const TAX_ID_TYPE_CHOICE_SSN_FIELD_NAME = 'Social Security Number (SSN)';
export const TAX_ID_TYPE_CHOICE_EMPTY_FIELD_NAME =
  'I don’t have my FEIN/SSN on hand right now (Skip for now)';
export const TAX_ID_TYPE_CHOICE_FEIN_FIELD_NAME = 'Federal Employer Identification Number (FEIN)';

export const UNEMPLOYMENT_NUMBER_UW_QUESTION = {
  questionCode: 'hasUnemploymentNumber',
  questionLabel: 'Do you have an Unemployment Registration Number (UI)?',
  fieldCode: 'UnemploymentNumber',
  fieldLabel: 'Unemployment Registration Number (UI)',
  fieldHelpText:
    "If you are a new employer and have applied for but not yet received a number, enter '0'.",
};

export const FEIN_FIELD_FOR_MANAGEMENT_FINANCIALS: Field = {
  code: 'fein',
  name: 'Federal Employer Identification Number (FEIN)',
  question_text: 'Please enter your Federal Employer Identification Number',
  is_optional: false,
  is_hidden: false,
  is_readonly: false,
  newline: false,
  type: 'string',
  additional_data: {
    socotra_field_code: 'pol_fein',
  },
  show_condition: {
    operations: [
      {
        reference: 'numEmployees',
        relational_operator: '>=',
        value: 6,
        value_type: 'number',
      },
      {
        reference: 'yearFounded',
        relational_operator: '<=',
        value: DateTime.now().year - 3,
        value_type: 'number',
      },
    ],
    logical_operator: 'and',
  },
};

/**
 * Checks if a submission requires one property based on:
 * - If they operate from commercial space
 */
export const submissionRequiresOneProperty = (submission?: TransformedSubmission) =>
  submission?.data?.uw_questions?.operatesFromCommercialSpace === 'Yes' &&
  isEmpty(submission?.data?.exposures?.building);

/**
 * Checks if a submission requires two owners based on:
 * - If they are a partnership
 */
export const submissionRequiresTwoOwners = (submission?: TransformedSubmission) =>
  submission?.data?.uw_questions?.entityType === 'Partnership' &&
  submission?.data?.groups?.pol_oo?.length < 2;

// A page should be associated with only one object.
export const skipConditionsData: SkipCondition[] = [
  {
    stepKeyList: [APPLICATION_STEPS.drivers.key, APPLICATION_STEPS['driving-incidents'].key],
    ignoreIf: (submission) =>
      isEmpty(submission?.data.exposures.vehicle) ||
      shouldSkipDriversStep(submission) ||
      submissionRequiresOneProperty(submission) ||
      submissionRequiresTwoOwners(submission),
  },
  {
    stepKeyList: [APPLICATION_STEPS.individual_owners.key],
    ignoreIf: (submission) =>
      submissionRequiresOneProperty(submission) ||
      submissionRequiresTwoOwners(submission) ||
      getManualPremiumPerilsFromSubmission(submission).length < 1,
  },
  {
    stepKeyList: [APPLICATION_STEPS.workers_compensation.key],
    ignoreIf: (submission) =>
      !submission?.data?.uw_questions?.numEmployees ||
      parseInt(submission.data.uw_questions.numEmployees, 10) === 0 ||
      !submission?.data?.uw_questions?.payrollW2 ||
      Number(submission.data.uw_questions.payrollW2) === 0 ||
      getManualPremiumPerilsFromSubmission(submission).length <= 1 ||
      submissionRequiresOneProperty(submission) ||
      submissionRequiresTwoOwners(submission),
  },
  {
    stepKeyList: [APPLICATION_STEPS.properties.key],
    ignoreIf: (submission) =>
      submission?.data?.uw_questions?.operatesFromCommercialSpace === 'No' ||
      submissionRequiresTwoOwners(submission),
  },
  {
    stepKeyList: [APPLICATION_STEPS['before-property'].key],
    ignoreIf: (submission) => submissionRequiresTwoOwners(submission),
  },
  {
    stepKeyList: [APPLICATION_STEPS['uw-property'].key, APPLICATION_STEPS.claims.key],
    ignoreIf: (submission) =>
      submissionRequiresOneProperty(submission) || submissionRequiresTwoOwners(submission),
  },
  {
    stepKeyList: [APPLICATION_STEPS.vehicles.key],
    ignoreIf: (submission, stepSkipCondition) =>
      submissionRequiresOneProperty(submission) ||
      submissionRequiresTwoOwners(submission) ||
      Boolean(stepSkipCondition),
  },
  {
    stepKeyList: [APPLICATION_STEPS.vehicles_injury_protection.key],
    ignoreIf: (submission) =>
      submission?.us_state !== 'MI' || isEmpty(submission?.data?.exposures?.vehicle),
  },
];

export const filloutFormShowConditions: FilloutFormShowCondition[] = [
  (submission) =>
    submission?.us_state === STATES.MI &&
    !isEmpty(submission?.data?.exposures?.vehicle) &&
    submission?.is_additional_information_missing === true,
];

export const DEFAULT_TOGGLE_BUTTONS_GROUP_OPTIONS = [
  { label: 'Yes', value: 'Yes' },
  { label: 'No', value: 'No' },
];

export const DEFAULT_AUTOPAY_TOGGLE_BUTTONS_GROUP_OPTIONS = [
  { label: 'Enroll in Autopay', value: 'Yes' },
  { label: 'Manual Payments (Nonrecurring)', value: 'No' },
];

export const DEFAULT_AUTOPAY_TOGGLE_BUTTONS_GROUP_OPTIONS_NEW = [
  {
    label: 'Enroll in Autopay',
    value: 'Yes',
    endAdornment: {
      src: AutoPaymentImg,
      type: 'icon',
    },
  },
  {
    label: 'Single Payment',
    value: 'No',
    endAdornment: {
      src: CoinsImg,
      type: 'icon',
    },
  },
];

export const EARTHQUAKE_COVERAGE_NAMES = [
  'building_earthquake',
  'other_property_earthquake',
  'business_interruption_earthquake',
];

export const genericErrorMessage =
  'Could not complete your request. If the problem persists, contact your support team.';

export const addVehicleProhibitedErrorMessage =
  'THREE cannot insure private vehicles for sole proprietors in NJ, NC, MI, or NY.';

export const duplicatedBuildingNameErrorMessage = 'Building name already in use.';

export const SCHEDULED_BUILDING_OCCUPANCY_VALUES = [
  "Building I lease and am required to insure (e.g. 'Triple Net Lease')",
  'Building I own and run my business out of',
  'Building I own/manage and lease to others',
];

export const HAS_SCHEDULED_BUILDINGS_CODE = 'hasScheduledBuildings';
export const BDG_OCCUPANCY_CODE = 'bdg_occupancy';

// Questionset page codes: group codes are neither unique nor stable, so the page is the only reliable key.
export const PREMISES_FORM_CODE = 'premises';
export const VEHICLE_FORM_CODE = 'vehicle';
// Stamped on the merged `vehicle` page group; the `premises` one reuses the `uw-property` step key.
export const UW_VEHICLE_GROUP_CODE = 'uw-vehicle';

// `hasTBI` has its own show condition that checks if any building
// has an occupancy type listed in `TBI_BUILDING_OCCUPANCY_VALUES`.
// However, this condition needs to be adjusted to work properly without
// modifying the `QuestionEngine`.
// `HAS_TBI_BUILDING_CODE` is a boolean flag that indicates whether
// any building satisfies the occupancy condition mentioned above.
export const HAS_TBI_CODE = 'hasTBI';
export const TBI_AMOUNT = 'tbiAmount';
export const HAS_TBI_BUILDING_CODE = 'hasTBIBuildings';
export const TBI_BUILDING_OCCUPANCY_VALUES = [
  'Commercial condo I lease',
  'Building I lease and am not required to insure',
];

export const ONLY_HOME_OFFICE_CODE = 'onlyHomeOffice';
export const ONLY_HOME_OFFICE_OPTION = 'Home/Apartment';

export const OPERATES_FROM_COMMERCIAL_SPACE_CODE = 'operatesFromCommercialSpace';

export const AVERAGE_ROOM_RATE_CODE = 'avgRoomRate';

export const PAYROLL_W2_CODE = 'payrollW2';

export const HAS_BPP_CODE = 'hasBPP';
export const BPP_AMOUNT_CODE = 'bppAmount';

export const HAULS_NON_OWNED_GOODS_VEHICLE = 'haulsNonOwnedGoodsVehicle';
export const IS_ANY_HAULS_NON_OWNED_GOODS_VEHICLE = 'isHaulsNonOwnedGoodsVehicle';
export const NUM_VEHICLES_CODE = 'numVehicles';

export const LICENSE_PLATE_STATE = 'licensePlateState';

/* Google Tag Manager */
export const GOOGLE_TAG_EVENTS = {
  LEAD_LEGACY: 'LeadLegacy',
  LEAD_ELIGIBLE: 'LeadEligible',
  LEAD_CREATED: 'LeadCreated',
  LEAD_QUOTED: 'LeadQuoted',
  LEAD_REFERRED: 'LeadReferred',
  LEAD_DECLINED: 'LeadDeclined',
  LEAD_COMPLETE: 'LeadComplete',
  PAYMENT_COMPLETE: 'Payment_Complete',
} as const;

export const FULLSTORY_EVENTS = {
  BEGIN_QUOTE_CREATE: 'BeginQuoteCreate',
  IGNORED_KEYWORD_SUGGESTIONS: 'IgnoredKeywordSuggestions',
  BEGIN_ABOUT_YOU_SECTION: 'BeginAboutYouSection (Step 1)',
  BEGIN_ABOUT_YOUR_BUSINESS_SECTION: 'BeginAboutYourBusinessSection (Step 2)',
  BEGIN_MANAGEMENT_FINANCIALS_SECTION: 'BeginManagementFinancialsSection (Step 3)',
  BEGIN_LOCATION_SECTION: 'BeginLocationSection (Step 4)',
  BEGIN_PROHIBITED_EXPOSURES_SECTION: 'BeginProhibitedExposuresSection (Step 5)',
  BEGIN_CLASSIFICATION_SECTION: 'BeginClassificationSection (Step 6)',
  BEGIN_PROPERTY_SECTION: 'BeginPropertySection (Step 7)',
  BEGIN_LIABILITY_SECTION: 'BeginLiabilitySection (Step 8)',
  BEGIN_PROFESSIONAL_SERVICES_SECTION: 'BeginProfessionalServicesSection (Step 9)',
  BEGIN_CYBER_SECTION: 'BeginCyberSection (Step 10)',
  BEGIN_EMPLOYEE_SUBCONTRACTOR_ACTIVITIES_SECTION: 'BeginEmpSubActivitiesSection (Step 11)',
  BEGIN_AUTO_SECTION: 'BeginAutoSection (Step 12)',
  BEGIN_OWNERS_SECTION: 'BeginOwnersSection (Step 13)',
  BEGIN_BEFORE_PROPERTY_SECTION: 'BeginBeforePropertySection (Step 14)',
  BEGIN_PROPERTIES_SECTION: 'BeginPropertiesSection (Step 15)',
  BEGIN_UW_PROPERTY_SECTION: 'BeginUwPropertySection (Step 16)',
  BEGIN_WORKERS_COMPENSATION_SECTION: 'BeginWorkersCompensationSection (Step 17)',
  BEGIN_INDIVIDUAL_OWNERS_SECTION: 'BeginIndividualOwnersSection (Step 18)',
  BEGIN_VEHICLES_SECTION: 'BeginVehiclesSection (Step 19)',
  BEGIN_DRIVERS_SECTION: 'BeginDriversSection (Step 20)',
  BEGIN_DRIVING_INCIDENTS_SECTION: 'BeginDrivingIncidentsSection (Step 21)',
  BEGIN_CLAIMS_SECTION: 'BeginClaimsSection (Step 22)',
} as const;

export const EMAIL_REGX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,10}$/;

const CORPORATE_OFFICERS_DESC = {
  title: 'Corporate Officers',
  descriprion: 'corporate officer',
};

export const INDIVIDUAL_OWNDERS_PAGE_DESC = {
  // LLC
  LLC: {
    title: 'LLC Members',
    descriprion: 'LLC member',
  },
  // Owners
  SoleProprietor: {
    title: 'Owners',
    descriprion: 'owner',
  },
  // Partners
  Partnership: {
    title: 'Partners',
    descriprion: 'partner',
  },
  // Corporate Officers
  Corporation: CORPORATE_OFFICERS_DESC,
  CorporationSCorp: CORPORATE_OFFICERS_DESC,
  CorporationOther: CORPORATE_OFFICERS_DESC,
  NonprofitOrganization: CORPORATE_OFFICERS_DESC,
};

export const BINARY_CONSTANTS = {
  YES: 'Yes',
  NO: 'No',
};

export const errorCodes = {
  UNDERWRITING_QUESTIONS: 1090,
  MISSING_EXPOSURES_OR_FIELD_GROUPS: 1080,
  SUBMISSION_HAS_ALREADY_FINALIZED: 1016,
};

export const EFFECTIVE_DATE_ERROR_MESSAGE = 'Start date must be no earlier than tomorrow.';

export const PURCHASE_ACTION_DISABLED_TOOLTIP_MESSAGE =
  'Purchase functionality is not available at this moment.';

export const isPurchaseActionDisabled =
  import.meta.env.REACT_APP_CONSUMER_PURCHASE_ACTION_DISABLED === 'true' || false;

export const PURCHASE_BLOCKED_MISSING_DOCUMENTS_MESSAGE =
  'Additional documents are required before this policy can be purchased. Your agent will contact you to complete this.';

export const approvePropertyUWReportType = ['BUR', 'PDR', 'BFUR'];

export const propertyExposureAffectPremiumCalculationsFields = [
  'bdg_construction_code',
  'bdg_wind_symbol',
  'bdg_year_built',
  'bdg_sqft',
  'bdg_num_stories',
  'bdg_issprinklered',
];

export const coverageTableWCMessageVariants = {
  WITHOUT_WC: 'without_wc',
  WITH_WC_EX_OO: 'with_wc_ex_oo',
  NO_MESSAGE: 'no_message',
  WITHOUT_WC_SPECIAL: 'wc_not_available',
  WC_EXCLUDED_BY_RULE: 'wc_excluded_by_rule',
  WC_EXCLUDED_BY_INDUSTRY: 'wc_excluded_by_industry',
} as const;

export const WC_BLACKLIST_STATES = ['OH', 'NY'];

export type TCoverageTableWCMessageVariants =
  (typeof coverageTableWCMessageVariants)[keyof typeof coverageTableWCMessageVariants];

export const singleSelectableDrivingIncidentFieldNames = [
  'At Fault Accident',
  'At Fault Accident, <= $1000',
  'At Fault Accident, > $1000',
  'Not at fault accident: Rear-ended or Vehicle was Parked',
  'Not at fault accident: Other/Unknown',
];

export const BIBERK_LINKS = {
  trucking:
    'https://www.biberk.com/get-a-quote/trucking-commercial-auto?a=THREE-Referral&utm_campaign=THREE-referral&utm_source=THREE&utm_medium=paid-partner&utm_term=Other',
  general:
    'https://www.biberk.com/get-a-quote/compete-business-insurance-b?a=THREE-Referral&utm_campaign=THREE-referral&utm_source=THREE&utm_medium=paid-partner&utm_term=Other',
} as const;

export const entityTypes = {
  SOLE_PROPRIETOR: { code: 'SoleProprietor', name: 'Sole Proprietor' },
  LIMITED_PARTNERSHIP: {
    code: 'LimitedPartnership',
    name: 'Limited Partnership',
  },
  LLC: { code: 'LLC', name: 'LLC' },
  CORPORATION: { code: 'Corporation', name: 'Corporation' },
  CORPORATION_S_CORP: { code: 'CorporationSCorp', name: 'Corporation S-Corp' },
  CORPORATION_OTHER: { code: 'CorporationOther', name: 'Corporation Other' },
  NONPROFIT_ORG: { code: 'NonprofitOrganization', name: 'Nonprofit Organization' },
  PARTNERSHIP: { code: 'Partnership', name: 'Partnership' },
  TRUST: { code: 'Trust', name: 'Trust' },
};

export const CORPORATION_ENTITY_TYPES: string[] = [
  entityTypes.CORPORATION.code,
  entityTypes.CORPORATION_S_CORP.code,
  entityTypes.CORPORATION_OTHER.code,
];

export const COVERAGE_CALLOUTS_ADDITIONAL_CRITERIA: CoverageCalloutAdditionalCriteria = {
  checkOffersBenefits: (submission: TransformedSubmission) =>
    submission.data.uw_questions?.offersBenefits === 'Yes',
  checkHasOutsideInvestors: (submission: TransformedSubmission) =>
    submission.data.uw_questions?.hasOutsideInvestors === 'Yes',
  checkTaxCAIndustry: (submission: TransformedSubmission) =>
    submission.data.policy_data?.pol_tax_ca_industry === 'For-Hire Trucking',
  checkNumEmployees: (submission: TransformedSubmission) =>
    submission.data.uw_questions?.numEmployees > 0,
  checkAlcoholSales: (submission: TransformedSubmission) =>
    submission.data.uw_questions?.alcoholSales > 0,
};

export const COVERAGE_CALLOUTS: CoverageCallout[] = [
  {
    industry: null,
    subIndustry: null,
    additionalCriteria: COVERAGE_CALLOUTS_ADDITIONAL_CRITERIA.checkOffersBenefits,
    calloutParentCoverage: 'Business Liability',
    triggerText: 'Fiduciary & Employee Benefits Liability',
    detailsKey: 'businessDetails',
  },
  {
    industry: null,
    subIndustry: null,
    additionalCriteria: COVERAGE_CALLOUTS_ADDITIONAL_CRITERIA.checkHasOutsideInvestors,
    calloutParentCoverage: 'Business Liability',
    triggerText: 'Directors & Officers Liability',
    detailsKey: 'businessDetails',
  },
  {
    industry: 'Auto Services & Dealers',
    subIndustry: 'Service/Repair',
    additionalCriteria: COVERAGE_CALLOUTS_ADDITIONAL_CRITERIA.checkTaxCAIndustry,
    calloutParentCoverage: 'Business Liability',
    triggerText: 'On-Hook Liability',
    detailsKey: 'businessDetails',
  },
  {
    industry: 'Construction & Specialty Trade Contractors',
    subIndustry: null,
    additionalCriteria: null,
    calloutParentCoverage: 'All Other Property',
    triggerText: 'Tools & Equipment',
    detailsKey: 'allOtherPropertyDetails',
  },
  {
    industry: 'Construction & Specialty Trade Contractors',
    subIndustry: null,
    additionalCriteria: null,
    calloutParentCoverage: 'Business Liability',
    triggerText: 'Blanket Additional Insured',
    detailsKey: 'businessDetails',
  },
  {
    industry: 'Hospitality & Entertainment/Recreation',
    subIndustry: null,
    additionalCriteria: COVERAGE_CALLOUTS_ADDITIONAL_CRITERIA.checkNumEmployees,
    calloutParentCoverage: 'Business Liability',
    triggerText: 'Employment Practices Liability',
    detailsKey: 'businessDetails',
  },
  {
    industry: 'Hospitality & Entertainment/Recreation',
    subIndustry: 'Hospitality',
    additionalCriteria: COVERAGE_CALLOUTS_ADDITIONAL_CRITERIA.checkAlcoholSales,
    calloutParentCoverage: 'Business Liability',
    triggerText: 'Liquor Liability',
    detailsKey: 'businessDetails',
  },
  {
    industry: 'Hospitality & Entertainment/Recreation',
    subIndustry: 'Hospitality',
    additionalCriteria: null,
    calloutParentCoverage: 'All Other Property',
    triggerText: 'Equipment Breakdown',
    detailsKey: 'allOtherPropertyDetails',
  },
  {
    industry: 'Landscaping & Farming',
    subIndustry: null,
    additionalCriteria: null,
    calloutParentCoverage: 'All Other Property',
    triggerText: 'Tools & Equipment',
    detailsKey: 'allOtherPropertyDetails',
  },
  {
    industry: 'Landscaping & Farming',
    subIndustry: null,
    additionalCriteria: null,
    calloutParentCoverage: 'Business Liability',
    triggerText: 'Blanket Additional Insured',
    detailsKey: 'businessDetails',
  },
  {
    industry: 'Restaurant & Taverns',
    subIndustry: null,
    additionalCriteria: COVERAGE_CALLOUTS_ADDITIONAL_CRITERIA.checkNumEmployees,
    calloutParentCoverage: 'All Other Property',
    triggerText: 'Employee Dishonesty',
    detailsKey: 'allOtherPropertyDetails',
  },
  {
    industry: 'Restaurant & Taverns',
    subIndustry: null,
    additionalCriteria: null,
    calloutParentCoverage: 'All Other Property',
    triggerText: 'Equipment Breakdown',
    detailsKey: 'allOtherPropertyDetails',
  },
  {
    industry: 'Restaurant & Taverns',
    subIndustry: null,
    additionalCriteria: null,
    calloutParentCoverage: 'All Other Property',
    triggerText: 'Food Spoilage',
    detailsKey: 'allOtherPropertyDetails',
  },
  {
    industry: 'Restaurant & Taverns',
    subIndustry: null,
    additionalCriteria: COVERAGE_CALLOUTS_ADDITIONAL_CRITERIA.checkAlcoholSales,
    calloutParentCoverage: 'Business Liability',
    triggerText: 'Liquor Liability',
    detailsKey: 'businessDetails',
  },
  {
    industry: 'Restaurant & Taverns',
    subIndustry: null,
    additionalCriteria: COVERAGE_CALLOUTS_ADDITIONAL_CRITERIA.checkNumEmployees,
    calloutParentCoverage: 'Business Liability',
    triggerText: 'Employment Practices Liability',
    detailsKey: 'businessDetails',
  },
  {
    industry: 'Retail/Wholesale Stores (incl Food Stores)',
    subIndustry: 'Food/Drinks',
    additionalCriteria: null,
    calloutParentCoverage: 'All Other Property',
    triggerText: 'Food Spoilage',
    detailsKey: 'allOtherPropertyDetails',
  },
  {
    industry: 'Retail/Wholesale Stores (incl Food Stores)',
    subIndustry: null,
    additionalCriteria: COVERAGE_CALLOUTS_ADDITIONAL_CRITERIA.checkNumEmployees,
    calloutParentCoverage: 'All Other Property',
    triggerText: 'Employee Dishonesty',
    detailsKey: 'allOtherPropertyDetails',
  },
  {
    industry: 'Service Industries',
    subIndustry: 'Agencies',
    additionalCriteria: null,
    calloutParentCoverage: 'Business Liability',
    triggerText: 'Professional E&O Liability',
    detailsKey: 'businessDetails',
  },
  {
    industry: 'Service Industries',
    subIndustry: 'Agencies',
    additionalCriteria: null,
    calloutParentCoverage: 'Business Liability',
    triggerText: 'Cyber Liability',
    detailsKey: 'businessDetails',
  },
  {
    industry: 'Service Industries',
    subIndustry: 'Business and Admin',
    additionalCriteria: null,
    calloutParentCoverage: 'Business Liability',
    triggerText: 'Professional E&O Liability',
    detailsKey: 'businessDetails',
  },
  {
    industry: 'Service Industries',
    subIndustry: 'Business and Admin',
    additionalCriteria: null,
    calloutParentCoverage: 'Business Liability',
    triggerText: 'Cyber Liability',
    detailsKey: 'businessDetails',
  },
  {
    industry: 'Service Industries',
    subIndustry: 'Financial Professionals',
    additionalCriteria: COVERAGE_CALLOUTS_ADDITIONAL_CRITERIA.checkNumEmployees,
    calloutParentCoverage: 'All Other Property',
    triggerText: 'Fraud & Embezzlement',
    detailsKey: 'allOtherPropertyDetails',
  },
  {
    industry: 'Service Industries',
    subIndustry: 'Financial Professionals',
    additionalCriteria: null,
    calloutParentCoverage: 'Business Liability',
    triggerText: 'Professional E&O Liability',
    detailsKey: 'businessDetails',
  },
  {
    industry: 'Service Industries',
    subIndustry: 'Financial Professionals',
    additionalCriteria: COVERAGE_CALLOUTS_ADDITIONAL_CRITERIA.checkNumEmployees,
    calloutParentCoverage: 'Business Liability',
    triggerText: 'Employment Practices Liability',
    detailsKey: 'businessDetails',
  },
  {
    industry: 'Service Industries',
    subIndustry: 'Financial Professionals',
    additionalCriteria: null,
    calloutParentCoverage: 'Business Liability',
    triggerText: 'Cyber Liability',
    detailsKey: 'businessDetails',
  },
  {
    industry: 'Service Industries',
    subIndustry: 'Installation/Repair',
    additionalCriteria: null,
    calloutParentCoverage: 'All Other Property',
    triggerText: 'Tools & Equipment',
    detailsKey: 'allOtherPropertyDetails',
  },
  {
    industry: 'Service Industries',
    subIndustry: 'Personal Services',
    additionalCriteria: COVERAGE_CALLOUTS_ADDITIONAL_CRITERIA.checkNumEmployees,
    calloutParentCoverage: 'All Other Property',
    triggerText: 'Employee Dishonesty',
    detailsKey: 'allOtherPropertyDetails',
  },
  {
    industry: 'Service Industries',
    subIndustry: 'Personal Services',
    additionalCriteria: COVERAGE_CALLOUTS_ADDITIONAL_CRITERIA.checkNumEmployees,
    calloutParentCoverage: 'Business Liability',
    triggerText: 'Employment Practices Liability',
    detailsKey: 'businessDetails',
  },
  {
    industry: 'Service Industries',
    subIndustry: 'Personal Services',
    additionalCriteria: null,
    calloutParentCoverage: 'Business Liability',
    triggerText: 'Professional E&O Liability',
    detailsKey: 'businessDetails',
  },
  {
    industry: 'Service Industries',
    subIndustry: 'Property Management and Janitorial/Cleaning Services',
    additionalCriteria: COVERAGE_CALLOUTS_ADDITIONAL_CRITERIA.checkNumEmployees,
    calloutParentCoverage: 'All Other Property',
    triggerText: 'Employee Dishonesty',
    detailsKey: 'allOtherPropertyDetails',
  },
  {
    industry: 'Service Industries',
    subIndustry: 'Property Management and Janitorial/Cleaning Services',
    additionalCriteria: null,
    calloutParentCoverage: 'All Other Property',
    triggerText: 'Tools & Equipment',
    detailsKey: 'allOtherPropertyDetails',
  },
  {
    industry: 'Service Industries',
    subIndustry: 'Property Management and Janitorial/Cleaning Services',
    additionalCriteria: COVERAGE_CALLOUTS_ADDITIONAL_CRITERIA.checkNumEmployees,
    calloutParentCoverage: 'Business Liability',
    triggerText: 'Janitorial Service Bond Liability',
    detailsKey: 'businessDetails',
  },
  {
    industry: 'Transportation & Warehousing',
    subIndustry: 'Transportation',
    additionalCriteria: COVERAGE_CALLOUTS_ADDITIONAL_CRITERIA.checkTaxCAIndustry,
    calloutParentCoverage: 'Business Liability',
    triggerText: 'Cargo Liability',
    detailsKey: 'businessDetails',
  },
];

export const PIP_MEDICAL_CHOICES_DESCRIPTION = [
  {
    title: 'Option 1: Unlimited Coverage',
    description:
      'This option provides the most coverage. It will pay for all allowable expenses without being subject to an aggregate dollar limit for care, recovery, and rehabilitation if a person covered under this policy is injured in an auto accident.',
    risks: 'The premiums for this option are higher than premiums for other options.',
    benefits:
      'PIP medical will cover costs that may not be covered by health insurance, such as rehabilitation and attendant care. This choice will significantly limit the risk that anyone covered under this policy will have out-of-pocket costs for their care.',
  },
  {
    title:
      'Option 2: Coverage up to $500,000 per person per accident OR Option 3: Coverage up to $250,000 per person per accident',
    description:
      'If you choose one of these limits, this amount is the most your auto insurance company will pay per person per accident for an injured person’s expenses under PIP medical coverage.',
    note: 'Your insurance company must offer excess attendant care coverage, which you may purchase for an additional premium. Check with your agent or company for additional information.',
    risks:
      'Limited PIP medical coverages may not be enough to cover medical expenses. If the PIP medical limit is reached, an injured person may need to rely on other health coverage, which may not cover all medical, rehabilitation, or attendant care costs. If an injured person does not have other health coverage, they may be personally responsible for paying these expenses.',
    benefits:
      'Lower coverage limits have less expensive premiums than plans with higher or unlimited PIP medical coverage. Up to the limit chosen, PIP medical will cover the cost of products and services that may not be covered by health insurance, such as rehabilitation and attendant care.',
  },
];

export const POL_PIP_MI_TOTAL_MEDICAL_EXPENSES_DEFAULT = '99999999';

export const wcExcludedIndustryNames = [
  'Arborist',
  'Landscape Gardening - w/ Tree Removal or Excavation',
  'Lawn Care Services - w/ Tree Removal or Excavation',
  'Lawn Maintenance - w/ Tree Removal or Excavation',
  'Orchard Work, Fumigating or Pruning',
  'Tree Pruning, Spraying, Repairing - No Removal or Excavation',
  'Tree Removal (not logging)',
  'Tree Services - No Removal or Excavation',
  'Tree Services - w/ Removal or Excavation',
  'Tree Trimming - No Removal or Excavation',
  'Tree Trimming  w/ Removal or Excavation',
  'Stump Grinding',
  'Window Washing - 3 stories or less',
  'Window Washing - 4 stories or more',
  'Chimney Sweep',
  'Window Cleaning',
  'Solar Panel Cleaning',
  'Roofer',
  'Roofing Contractor',
  'Satellite Dish Installation',
  'Shingling',
  'Solar Panel Installation',
  'DirecTV Installation',
  'Construction: Specialty; Roofing',
  'Solar Panel Maintenance; No Installation',
  'Holiday Lighting and Decorating',
];

export const WC_STATE_EXCLUSION_MESSAGES: Record<string, string> = {
  OH: `This quote will not include Workers' Compensation coverage. Coverage must be purchased through the ${STATES_FULLNAME.OH} Bureau of Workers’ Compensation.`,
  NY: `This quote will not include Workers' Compensation coverage. THREE does not offer Workers' Compensation coverage in ${STATES_FULLNAME.NY}.`,
};

export const IS_OUTBUILDING_FIELD_CODE = 'isOutbuilding';
export const IS_SPRINKLER_INSPECTED_ANNUALLY_FIELD_CODE = 'isSprinklerInspectedAnnually';
export const SPRINKLER_INSPECTION_ACKNOWLEDGEMENT_FIELD_CODE = 'sprinklerInspectionAcknowledgement';

export const PROPERTIES_UW_EXCLUDED_FIELD_CODES = [
  IS_OUTBUILDING_FIELD_CODE,
  IS_SPRINKLER_INSPECTED_ANNUALLY_FIELD_CODE,
  SPRINKLER_INSPECTION_ACKNOWLEDGEMENT_FIELD_CODE,
];

//https://linear.app/three-insurance/issue/ENG-9473/consumer-portal-update-vehicledriver-validation-for-v171-increase
export const DRIVER_VALIDATION_MESSAGE = (vehicleMaxCount: number) =>
  `There must be at least one driver if the number of vehicles is 1 to ${vehicleMaxCount}.`;

/**
 * Version threshold for driver validation rules (V17.1).
 * Format: major * 100 + minor (e.g., V17.1 = 1701, V14.12 = 1412)
 * Versions >= this value allow up to 10 vehicles, earlier versions allow up to 5.
 */
export const DRIVER_VEHICLE_VALIDATION_LOWER_LIMIT_VERSION = 1701;

/**
 * Industry payroll thresholds for low payroll per employee worksheet.
 * Keys must match pol_tax_industry values from submission policy_data.
 */
export const industryPayrollThreshold: Record<string, number> = {
  'Auto Services & Dealers': 10000,
  'Construction & Specialty Trade Contractors': 10000,
  'Education & Public Works/Organizations': 7500,
  'Health Care & Social Assistance': 10000,
  'Hospitality & Entertainment/Recreation': 5000,
  'Landscaping & Farming': 10000,
  Manufacturing: 10000,
  'Retail/Wholesale Stores (incl Food Stores)': 7500,
  'Restaurant & Taverns': 5000,
  'Service Industries': 7500,
  'Transportation & Warehousing': 12500,
};

export const IS_PAYROLL_WORKSHEET_ENABLED = [
  'training',
  'production',
  'qa',
  'development',
].includes(import.meta.env.MODE);

export const weeksPerYearValue = 50;
export const weeksPerMonthValue = 4.17;
export const operationalMonthsSeparator = '|';

/** Hotel/hospitality subtypes that use the expanded payroll worksheet. */
export const EXPANDED_HOTEL_PAYROLL_WORKSHEET_INDUSTRIES: readonly string[] = [
  'Hospitality: Hotels',
  'Hotel - No Restaurant',
  'Hotel - w/ Restaurant',
  'Inn - No Restaurant',
  'Inn - w/ Restaurant',
  'Motels - no restaurant',
  'Motels - w/ restaurant',
];

/** Additional UW field codes for expanded hotel worksheet (year-round employees). */
export const EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES = {
  hasYearRoundEmployees: 'hasYearRoundEmployees',
  yearRoundFtEmployeeCount: 'yearRoundFtEmployeeCount',
  yearRoundFtAvgHourlyWage: 'yearRoundFtAvgHourlyWage',
  yearRoundFtAvgWeeklyHours: 'yearRoundFtAvgWeeklyHours',
  yearRoundFtEstimatedAnnualPayroll: 'yearRoundFtEstimatedAnnualPayroll',
  yearRoundPtEmployeeCount: 'yearRoundPtEmployeeCount',
  yearRoundPtAvgHourlyWage: 'yearRoundPtAvgHourlyWage',
  yearRoundPtAvgWeeklyHours: 'yearRoundPtAvgWeeklyHours',
  yearRoundPtEstimatedAnnualPayroll: 'yearRoundPtEstimatedAnnualPayroll',
} as const;

export const EXPANDED_HOTEL_YEAR_ROUND_INPUT_CODES: string[] = [
  EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundFtEmployeeCount,
  EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundFtAvgHourlyWage,
  EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundFtAvgWeeklyHours,
  EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundFtEstimatedAnnualPayroll,
  EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundPtEmployeeCount,
  EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundPtAvgHourlyWage,
  EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundPtAvgWeeklyHours,
  EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundPtEstimatedAnnualPayroll,
];

export const FOOD_TRUCK_TYPE_FIELD_CODE = 'foodTruckType';

const FOOD_TRUCK_VEHICLE_REQUIRED_BLOCK = {
  condition: (submission: TransformedSubmission | undefined) =>
    isEmpty(submission?.data?.exposures?.vehicle),
  validationErrorMessage:
    'Food truck/trailer operation must have a vehicle added. It was indicated that the business operates a self-powered food truck or a food trailer, but no vehicle has been added. Please add a vehicle or trailer before binding.',
};

export const VEHICLE_TAB_PROGRESSION_BLOCK_CONDITIONS_BY_UW_QUESTION_ANSWER = {
  [FOOD_TRUCK_TYPE_FIELD_CODE]: {
    'Food truck (self-powered vehicle)': FOOD_TRUCK_VEHICLE_REQUIRED_BLOCK,
    'Food trailer (towed by a vehicle)': FOOD_TRUCK_VEHICLE_REQUIRED_BLOCK,
  },
} as Record<
  string,
  Record<
    string,
    {
      condition: (submission: TransformedSubmission | undefined) => boolean;
      validationErrorMessage: string;
    }
  >
>;
